import { useEffect,useRef } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";

import { useGetVideosByApiQuery } from "../../services/modulesuser/videoListApi/index.js";
import { titleToKey } from "../../services/utils/helpers.js";
import { setVideosByKey } from "../../store/video";

import Loader from "../../component/ui/Loader.js";
import Heading from "../../component/ui/Heading.js";
import ButtonsPreview from "../../component/previews/ButtonsPreview.js";
import PreviewImage from "../../component/previews/PreviewImage.js";

import useSwipeScroll from "../../hooks/useSwipeScroll.js";

function VideoNumberCard({ video,index }) {
  const number = index + 1;
  const hasDoubleDigits = number >= 10;
  const numberColumnClass = hasDoubleDigits
    ? "w-[116px] sm:w-[136px] md:w-[150px]"
    : "w-[76px] sm:w-[94px] md:w-[104px]";

  return (
    <div className="relative flex flex-shrink-0 items-start">
      <div className={`${numberColumnClass} flex h-[124px] sm:h-[152px] md:h-[180px] flex-shrink-0 items-center justify-end pr-2 sm:pr-3 md:pr-4`}>
        <div className="relative z-0 text-white number-outline number-font font-montserrat text-[100px] sm:text-[122px] md:text-[146px] lg:text-[160px] font-black italic leading-[0.85] pointer-events-none select-none">
          {number}
        </div>
      </div>

      <div className="relative z-10 -ml-6 sm:-ml-8 md:-ml-10 block w-[220px] sm:w-[260px] md:w-[300px] lg:w-[320px] flex-shrink-0">
        <Link
          to={`/videos/${video.id}`}
          state={{ video }}
          className="block w-full"
        >
          <PreviewImage media={video} type="tv" hud />
        </Link>
      </div>
    </div>
  );
}

function VideoRowNumber({ title,api }) {
  const dispatch = useDispatch();
  const rowRef = useRef(null);

  // Abilita swipe/drag/wheel-to-horizontal sul container
  useSwipeScroll(rowRef,{ friction: 1,enableWheelX: true });

  const { data,isLoading,error } = useGetVideosByApiQuery(api);

  // Normalizzazione dati da API (copre /user/recommended, /user/top5, /user/getlatestvideosbynetwork)
  const videos = (() => {
    const result = data?.data?.result;

    // Case 1: array semplice (es. recommended/top5) con elementi { video: {...} }
    if (Array.isArray(result)) {
      return result.map((entry) => ({
        id: entry.video.id,
        type: entry.video.type,
        title: entry.video.title,
        thumbnail: entry.video.thumbnail,
        rating: entry.video.rating || 8,
        url: entry.video.url,
        description: entry.video.description,
        createdAt: entry.video.createdAt,
        categories: Array.isArray(entry.video.categories) ? entry.video.categories : [],
      }));
    }

    // Case 2: oggetto con { videos: [...] }
    if (Array.isArray(result?.videos)) {
      return result.videos.map((video) => ({
        id: video.id,
        type: video.type,
        title: video.title,
        thumbnail: video.thumbnail,
        rating: video.rating || 0,
        url: video.url,
        description: video.description,
        createdAt: video.createdAt,
        categories: Array.isArray(video.categories) ? video.categories : [],
      }));
    }

    // Fallback
    return [];
  })();

  // Salva in Redux per chiave derivata dal titolo
  useEffect(() => {
    dispatch(
      setVideosByKey({
        key: titleToKey(title),
        videos: videos || [],
        title,
      })
    );
  },[dispatch,title,videos]);

  if (isLoading) return <Loader />;
  if (error || !videos.length) return null;

  return (
    <section className="mt-8">
      <Heading>{title}</Heading>

      <div className="relative" data-xscroll>
        <ButtonsPreview
          ribbon={rowRef}
          length={videos.length}
          isScrollByOne={true}
        />

        <div
          data-xscroll
          ref={rowRef}
          className="
          flex overflow-x-auto scroll-smooth scrollbar-hide mt-3
          gap-[24px] sm:gap-[28px] md:gap-[34px] items-center cursor-grab select-none
          "
          style={{
            WebkitOverflowScrolling: "touch",
            touchAction: "pan-x",
            overscrollBehaviorX: "contain",
          }}
        >
          {videos.map((video,index) => (
            <VideoNumberCard key={video.id} video={video} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default VideoRowNumber;
